import React, { createContext, useCallback, useEffect, useMemo } from 'react';
import useGetTenantDetails from '@hooks/requests/tenant-details.get.hook';
import useGetVisit from '@hooks/requests/visit.get.hook';
import useGreedyState from '@hooks/use-greedy-state.hook';
import useVisitPatch from '@hooks/requests/visit-patch-hook';
import TechnicianSortableAttributes from '@enums/sortable-attributes/technician-sortable-attributes.enum';
import SubcontractorSortableAttributes from '@enums/sortable-attributes/subcontractor-sortable-attributes.enum';
import useGetTechnicians from '@hooks/requests/technicians.get.hook';
import useGetSubcontractors from '@hooks/requests/subcontractors.get.hook';
import useGetVisitStatuses from '@hooks/requests/visit-statuses.get.hook';
import useGetVisitPriorities from '@hooks/requests/visit-priorities.get.hook';
import useGetTimeZones from '@hooks/requests/time-zones.get.hook';
import { VisitableType } from '@enums/visitable-type-enum';
import { AssigneeType } from '@enums/assignee-type.enum';
const actionNotProvided = () => {
    console.warn('Action not provided to context');
};
const defaultContext = {
    modalReady: false,
    modalError: null,
    tenant: null,
    visit: null,
    saving: false,
    visitStatusList: [],
    visitPriorityList: [],
    subcontractorList: [],
    technicianList: [],
    timeZoneList: [],
    closeModal: actionNotProvided,
    saveVisit: actionNotProvided
};
export const VisitModalContext = createContext(defaultContext);
export function VisitModalContextProvider(params) {
    // Load Tenant Details
    const { data: tenant, loading: tenantLoading, error: tenantError } = useGetTenantDetails();
    // Load Visit
    const { data: fetchedVisit, loading: visitLoading, error: visitError } = useGetVisit(params.id);
    const { data: visit, setData: setVisit } = useGreedyState(fetchedVisit);
    const { save: saveVisit, saving, result: savedVisit } = useVisitPatch();
    const { techniciansParams, subcontractorsParams, statusesParams, prioritiesParams, timeZonesParams } = useMemo(() => {
        var _a;
        let techParams;
        let subParams;
        let statParams;
        let priorityParams;
        let timeZoneParams;
        if (!visit)
            return { techniciansParams: techParams, subcontractorsParams: subParams, statusesParams: statParams, prioritiesParams: priorityParams, timeZonesParams: timeZoneParams };
        const assignedTechId = visit.assignee.type === AssigneeType.TECHNICIAN && visit.assignee.info ? visit.assignee.info.id : null;
        techParams = {
            select: 'all',
            sort: { attribute: TechnicianSortableAttributes.NAME, direction: 'asc' },
            filter: Object.assign(Object.assign(Object.assign({}, (visit.visitable.type === VisitableType.INSPECTION && { forInspections: true })), (((_a = visit.building) === null || _a === void 0 ? void 0 : _a.id) && { buildingId: visit.building.id })), (assignedTechId && { ids: [assignedTechId] }))
        };
        subParams = {
            select: 'all',
            filter: {
                visitTypes: [visit.visitable.type]
            },
            sort: { attribute: SubcontractorSortableAttributes.NAME, direction: 'asc' }
        };
        statParams = {
            select: 'all',
            filter: {
                type: visit.visitable.type
            }
        };
        priorityParams = {
            select: 'all'
        };
        timeZoneParams = {
            select: 'all',
            filter: {
                resourceType: 'Visit',
                resourceId: visit.id
            }
        };
        return { techniciansParams: techParams, subcontractorsParams: subParams, statusesParams: statParams, prioritiesParams: priorityParams, timeZonesParams: timeZoneParams };
    }, [visit]);
    // Load Visit Statuses
    const { data: visitStatusList, loading: visitStatusesLoading, error: visitStatusesError } = useGetVisitStatuses(statusesParams);
    // Load Visit Priorities
    const { data: visitPriorityList, loading: visitPrioritiesLoading, error: visitPrioritiesError } = useGetVisitPriorities(prioritiesParams);
    // Load Timezones
    const { data: timeZoneList, loading: timeZonesLoading, error: timeZonesError } = useGetTimeZones(timeZonesParams);
    // Load Technicians
    const { data: technicianList, loading: techniciansLoading, error: techniciansError } = useGetTechnicians(techniciansParams);
    // Load Subcontractors conditionally on tenant flag
    const { data: subcontractorList, loading: subcontractorsLoading, error: subcontractorsError } = useGetSubcontractors(subcontractorsParams, !!(tenant === null || tenant === void 0 ? void 0 : tenant.flags.subcontractorsEnabled));
    const modalDataInitializing = tenantLoading || visitLoading || techniciansLoading || subcontractorsLoading || visitStatusesLoading || visitPrioritiesLoading || timeZonesLoading;
    const modalInitializationError = tenantError || visitError || techniciansError || subcontractorsError || visitStatusesError || visitPrioritiesError || timeZonesError;
    const handleCloseModal = useCallback(() => {
        params.onClose();
    }, []);
    useEffect(() => {
        if (savedVisit) {
            setVisit(savedVisit);
            params.onSave(savedVisit);
        }
    }, [savedVisit]);
    const contextValue = useMemo(() => {
        const value = {
            modalReady: !modalDataInitializing,
            modalError: modalInitializationError !== null && modalInitializationError !== void 0 ? modalInitializationError : null,
            tenant: tenant !== null && tenant !== void 0 ? tenant : null,
            visit: visit !== null && visit !== void 0 ? visit : null,
            saving,
            visitStatusList,
            visitPriorityList,
            subcontractorList,
            technicianList,
            timeZoneList,
            closeModal: handleCloseModal,
            saveVisit
        };
        return value;
    }, [
        tenant,
        visit,
        saveVisit,
        saving,
        visitStatusList,
        visitPriorityList,
        subcontractorList,
        technicianList,
        timeZoneList,
        modalDataInitializing,
        modalInitializationError
    ]);
    return React.createElement(VisitModalContext.Provider, { value: contextValue }, params.children);
}
